'use client';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { getAttempt } from './attempts';
import { isObservabilityLogsEnabled } from './config';
import type { ReportErrorMeta } from './observability.types';
import { extractHttpContext, redactString, scrubContext } from './scrub';

/**
 * Converte o que foi lançado em `Error` de verdade: é o que faz `addError`
 * produzir stack desminificado pelos sourcemaps. Mensagem redigida, stack
 * original preservada para o Error Tracking agrupar.
 */
function normalizeError(error: unknown): {
  normalized: Error;
  wasError: boolean;
} {
  if (error instanceof Error) {
    const normalized = new Error(redactString(error.message));
    normalized.name = error.name;
    if (error.stack) normalized.stack = error.stack;
    return { normalized, wasError: true };
  }

  if (typeof error === 'string') {
    return { normalized: new Error(redactString(error)), wasError: false };
  }

  // Objeto solto, `undefined` de um `.catch()` vazio, rejection sem valor.
  const message =
    typeof error === 'object' && error !== null
      ? redactString(String((error as { message?: unknown }).message ?? ''))
      : '';

  return {
    normalized: new Error(message || 'Erro não identificado'),
    wasError: false,
  };
}

/** Interceptor e `onError` podem ver o mesmo `AxiosError`; sem isto, duas issues. */
const REPORTED_FLAG = '__ddReported';

/**
 * Checagem pura, sem marcar. O interceptor de transporte usa isto para ceder a
 * vez ao `onError`, que sabe a etapa do funil — ver `instance.service.ts`.
 */
export function isReported(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    Boolean((error as Record<string, unknown>)[REPORTED_FLAG])
  );
}

function markReported(error: unknown): boolean {
  if (isReported(error)) return true;
  if (typeof error !== 'object' || error === null) return false;

  try {
    Object.defineProperty(error, REPORTED_FLAG, {
      value: true,
      enumerable: false,
    });
  } catch {
    // Objeto congelado: segue sem dedupe, melhor duplicar do que perder.
  }

  return false;
}

/**
 * Caminho único de saída de erro, em dois canais.
 *
 * RUM dá agrupamento, waterfall e replay, mas só em sessão amostrada — evento de
 * sessão não amostrada é descartado no browser. Logs cobrem 100% (amostragem
 * própria) e custam por volume, não por sessão. Sem o canal de Logs, ver a
 * maioria dos erros exigiria subir a amostragem do RUM.
 *
 * O contexto passa por `scrubContext`, e a parte de HTTP por allowlist em
 * `extractHttpContext` — ver o aviso em `scrub.ts`.
 *
 * Nunca lança.
 */
export function reportError(error: unknown, meta: ReportErrorMeta): void {
  try {
    if (markReported(error)) return;

    const { normalized, wasError } = normalizeError(error);
    const { source, ...rest } = meta;
    const attempt = getAttempt(source);

    const context = {
      // `error_source`, e nao `source`: `source` e atributo **reservado** no
      // Datadog Logs — e o que decide qual pipeline e quais facets sao aplicados
      // ao log (casa com nome de integracao, tipo `nginx`). Um valor nosso ali
      // sequestraria esse comportamento. O nome do campo na API publica do
      // modulo segue `source`, que e o que faz sentido para quem chama.
      error_source: source,
      ...extractHttpContext(error),
      ...(scrubContext(rest) as Record<string, unknown>),
      ...(wasError ? {} : { thrown_value_was_not_error: true }),
      ...(attempt !== undefined ? { attempt } : {}),
    };

    // `NODE_ENV` e nao `NEXT_PUBLIC_ENV`: stg tambem declara
    // `NEXT_PUBLIC_ENV: "development"` e sdx declara `"sandbox"`, entao por ali
    // nao ha como separar local de ambiente publicado — e sandbox e ambiente de
    // integrador, onde este console vazaria `api_error_messages` no browser do
    // cliente. `next dev` e o unico que roda fora de `NODE_ENV=production`.
    if (process.env.NODE_ENV !== 'production') {
      // Sem token de RUM localmente, o erro não apareceria em lugar nenhum.
      // eslint-disable-next-line no-console
      console.error(`[observability:${source}]`, normalized, context);
    }

    if (typeof window === 'undefined') return;

    datadogRum.addError(normalized, context);

    if (isObservabilityLogsEnabled()) {
      datadogLogs.logger.error(
        `[${source}] ${normalized.message}`,
        context,
        normalized
      );
    }
  } catch {
    // Falha de instrumentação não derruba o pagamento.
  }
}
